import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ─── Brand colours ──────────────────────────────────────────────────────────
const EMERALD_DARK  = [6,  78,  59];   // #064e3b
const EMERALD       = [16, 185, 129];  // #10b981
const WHITE         = [255, 255, 255];
const DARK_TEXT     = [15,  15,  15];
const MUTED_TEXT    = [100, 100, 100];
const LIGHT_BG      = [248, 250, 249];
const DIVIDER       = [220, 220, 220];

function periodLabel(days) {
  if (days === 1)  return 'Today';
  if (days === 7)  return 'Last 7 Days';
  return 'Last 30 Days';
}

// ─── CSV Export ─────────────────────────────────────────────────────────────
export function exportCSV(metrics, days) {
  if (!metrics) return;
  const label = periodLabel(days);
  const now = new Date().toLocaleString();
  const rows = [];

  rows.push(['AfroSports Stream — League Report']);
  rows.push([`Period: ${label}`]);
  rows.push([`Generated: ${now}`]);
  rows.push([]);

  rows.push(['=== KEY METRICS ===']);
  rows.push(['Metric', 'Value']);
  rows.push(['Total Registered Users', metrics.users.total]);
  rows.push([`Active Viewers (${label})`, metrics.users.active_in_period]);
  rows.push([`Video Plays (${label})`, metrics.watching.video_plays]);
  rows.push([`Unique Viewers (${label})`, metrics.watching.unique_viewers]);
  rows.push(['Avg Watch Time (est.)', metrics.watching.avg_watch_time]);
  rows.push(['Continue Watching Users', metrics.watching.continue_watching_count]);
  rows.push(['Subscribed Users', metrics.users.subscribed]);
  rows.push(['Subscription Rate', metrics.users.subscription_rate]);
  rows.push([]);

  rows.push(['=== RETENTION SIGNALS ===']);
  rows.push(['Metric', 'Value']);
  rows.push([`Reminders Set (${label})`, metrics.retention.reminders]);
  rows.push([`My List Adds (${label})`, metrics.retention.mylist_adds]);
  rows.push(['Live Viewers (now)', metrics.retention.live_viewers]);
  rows.push([]);

  if (metrics.most_watched_competition) {
    rows.push(['=== MOST WATCHED COMPETITION ===']);
    rows.push(['Competition', 'Plays', 'Unique Viewers']);
    rows.push([
      metrics.most_watched_competition.competition,
      metrics.most_watched_competition.plays,
      metrics.most_watched_competition.unique_viewers,
    ]);
    rows.push([]);
  }

  if (metrics.competition_watch?.length) {
    rows.push(['=== COMPETITION BREAKDOWN (7 DAYS) ===']);
    rows.push(['Competition', 'Plays', 'Unique Viewers']);
    metrics.competition_watch.forEach(c => rows.push([c.competition, c.plays, c.unique_viewers]));
    rows.push([]);
  }

  if (metrics.top_content?.length) {
    rows.push([`=== TOP CONTENT (${label}) ===`]);
    rows.push(['#', 'Match', 'Plays']);
    metrics.top_content.forEach((c, i) => rows.push([i + 1, c.title, c.play_count]));
    rows.push([]);
  }

  if (metrics.live_viewers_per_match?.length) {
    rows.push(['=== LIVE VIEWERS PER MATCH ===']);
    rows.push(['Match', 'Competition', 'Viewers Now']);
    metrics.live_viewers_per_match.forEach(m =>
      rows.push([m.title, m.competition, m.viewers])
    );
    rows.push([]);
  }

  if (metrics.trends?.viewer_growth?.length) {
    rows.push([`=== VIEWER GROWTH (${label}) ===`]);
    rows.push(['Time', 'Viewers', 'Plays']);
    metrics.trends.viewer_growth.forEach(g => rows.push([g.label, g.viewers, g.plays]));
    rows.push([]);
  }

  if (metrics.trends?.peak_hours?.length) {
    rows.push(['=== PEAK VIEWING HOURS ===']);
    rows.push(['Hour', 'Plays']);
    metrics.trends.peak_hours.forEach(h => rows.push([h.hour, h.plays]));
    rows.push([]);
  }

  const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const filename = `afrosports-report-${label.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0,10)}.csv`;
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Helpers for jsPDF ───────────────────────────────────────────────────────
function setFont(doc, size, style = 'normal', color = DARK_TEXT) {
  doc.setFont('helvetica', style);
  doc.setFontSize(size);
  doc.setTextColor(...color);
}

function drawHRule(doc, y, color = DIVIDER) {
  doc.setDrawColor(...color);
  doc.setLineWidth(0.3);
  doc.line(15, y, 195, y);
}

function sectionHeading(doc, text, y) {
  setFont(doc, 8, 'bold', EMERALD);
  doc.text(text.toUpperCase(), 15, y);
  drawHRule(doc, y + 2);
  return y + 8;
}

function kpiBox(doc, label, value, x, y, w = 42) {
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(x, y, w, 18, 2, 2, 'F');
  doc.setDrawColor(...DIVIDER);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, 18, 2, 2, 'S');
  setFont(doc, 7, 'normal', MUTED_TEXT);
  doc.text(label, x + w / 2, y + 6, { align: 'center' });
  setFont(doc, 13, 'bold', DARK_TEXT);
  doc.text(String(value ?? '—'), x + w / 2, y + 14, { align: 'center' });
}

async function captureChart(id) {
  const el = document.getElementById(id);
  if (!el) return null;
  try {
    const canvas = await html2canvas(el, {
      backgroundColor: '#141414',
      scale: 2,
      useCORS: true,
      logging: false,
    });
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

// ─── PDF Export ──────────────────────────────────────────────────────────────
export async function exportPDF(metrics, days, setExporting) {
  if (!metrics) return;
  setExporting(true);

  const label = periodLabel(days);
  const now = new Date();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210;

  // ── Page 1 ─────────────────────────────────────────────────────────────────

  // Header bar
  doc.setFillColor(...EMERALD_DARK);
  doc.rect(0, 0, W, 40, 'F');

  // Logo circle
  doc.setFillColor(...EMERALD);
  doc.circle(26, 20, 11, 'F');

  // Play triangle
  doc.setFillColor(...WHITE);
  doc.triangle(22, 14.5, 22, 25.5, 33, 20, 'F');

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...WHITE);
  doc.text('AFROSPORTS', 43, 16);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('STREAM', 43, 22);

  // Report tag
  doc.setFillColor(...EMERALD);
  doc.roundedRect(43, 25, 38, 7, 1.5, 1.5, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...WHITE);
  doc.text('NETWORK REPORT', 62, 30, { align: 'center' });

  // Period & date (right side)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(200, 230, 210);
  doc.text(`Period: ${label}`, W - 15, 18, { align: 'right' });
  doc.text(`Generated: ${now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`, W - 15, 24, { align: 'right' });
  doc.text(`${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} UTC`, W - 15, 30, { align: 'right' });

  let y = 52;

  // ── KPI Row ───────────────────────────────────────────────────────────────
  y = sectionHeading(doc, 'Key Performance Indicators', y);
  const kpis = [
    { label: 'Total Users',      value: metrics.users.total },
    { label: `Active (${label})`, value: metrics.users.active_in_period },
    { label: 'Unique Viewers',   value: metrics.watching.unique_viewers },
    { label: 'Avg Watch Time',   value: metrics.watching.avg_watch_time },
  ];
  kpis.forEach((kpi, i) => kpiBox(doc, kpi.label, kpi.value, 15 + i * 47, y));
  y += 26;

  const kpis2 = [
    { label: 'Video Plays',   value: metrics.watching.video_plays },
    { label: 'Subscribed',    value: metrics.users.subscribed },
    { label: 'Sub Rate',      value: metrics.users.subscription_rate },
    { label: 'Live Viewers',  value: metrics.retention.live_viewers },
  ];
  kpis2.forEach((kpi, i) => kpiBox(doc, kpi.label, kpi.value, 15 + i * 47, y));
  y += 28;

  // ── Most Watched Competition ───────────────────────────────────────────────
  if (metrics.most_watched_competition) {
    y = sectionHeading(doc, 'Most Watched Competition', y);
    doc.setFillColor(232, 248, 242);
    doc.roundedRect(15, y, W - 30, 16, 2, 2, 'F');
    setFont(doc, 11, 'bold', EMERALD_DARK);
    doc.text(metrics.most_watched_competition.competition, 24, y + 7);
    setFont(doc, 8, 'normal', MUTED_TEXT);
    doc.text(`${metrics.most_watched_competition.plays} plays · ${metrics.most_watched_competition.unique_viewers} unique viewers (last 7 days)`, 24, y + 13);
    y += 24;
  }

  // ── Competition Breakdown ─────────────────────────────────────────────────
  if (metrics.competition_watch?.length) {
    y = sectionHeading(doc, 'Competition Breakdown (7 Days)', y);
    const cols = [105, 155, 195];
    setFont(doc, 7.5, 'bold', MUTED_TEXT);
    doc.text('Competition', 20, y); doc.text('Plays', cols[0], y, { align: 'right' }); doc.text('Viewers', cols[1], y, { align: 'right' });
    y += 3;
    metrics.competition_watch.slice(0, 8).forEach((c, i) => {
      if (i % 2 === 0) { doc.setFillColor(248, 250, 249); doc.rect(15, y - 1, W - 30, 7, 'F'); }
      setFont(doc, 8, 'normal', DARK_TEXT);
      doc.text(String(i + 1) + '.  ' + c.competition, 20, y + 4);
      doc.text(String(c.plays), cols[0], y + 4, { align: 'right' });
      doc.text(String(c.unique_viewers), cols[1], y + 4, { align: 'right' });
      y += 7;
    });
    y += 6;
  }

  // ── Top Content ───────────────────────────────────────────────────────────
  if (metrics.top_content?.length) {
    if (y > 230) { doc.addPage(); y = 20; }
    y = sectionHeading(doc, `Top Content — ${label}`, y);
    const cols2 = [155, 195];
    setFont(doc, 7.5, 'bold', MUTED_TEXT);
    doc.text('Match', 20, y); doc.text('Plays', cols2[0], y, { align: 'right' });
    y += 3;
    metrics.top_content.forEach((c, i) => {
      if (i % 2 === 0) { doc.setFillColor(248, 250, 249); doc.rect(15, y - 1, W - 30, 7, 'F'); }
      setFont(doc, 8, 'normal', DARK_TEXT);
      doc.text(String(i + 1) + '.  ' + (c.title.length > 60 ? c.title.slice(0, 57) + '…' : c.title), 20, y + 4);
      doc.text(String(c.play_count), cols2[0], y + 4, { align: 'right' });
      y += 7;
    });
    y += 6;
  }

  // ── Live Viewers Per Match ────────────────────────────────────────────────
  if (metrics.live_viewers_per_match?.length) {
    if (y > 230) { doc.addPage(); y = 20; }
    y = sectionHeading(doc, 'Live Viewers Per Match', y);
    metrics.live_viewers_per_match.forEach((m, i) => {
      if (i % 2 === 0) { doc.setFillColor(248, 250, 249); doc.rect(15, y - 1, W - 30, 7, 'F'); }
      setFont(doc, 8, 'normal', DARK_TEXT);
      doc.text(m.title, 20, y + 4);
      setFont(doc, 8, 'normal', MUTED_TEXT);
      doc.text(m.competition || '', 100, y + 4);
      setFont(doc, 8, 'bold', EMERALD_DARK);
      doc.text(String(m.viewers) + ' viewers', 190, y + 4, { align: 'right' });
      y += 7;
    });
    y += 6;
  }

  // ── Page 2: Charts ─────────────────────────────────────────────────────────
  // Capture charts from DOM
  const [growthImg, peakImg, retentionImg] = await Promise.all([
    captureChart('chart-export-viewer-growth'),
    captureChart('chart-export-peak-hours'),
    captureChart('chart-export-retention'),
  ]);

  if (growthImg || peakImg || retentionImg) {
    doc.addPage();

    // Page 2 header strip
    doc.setFillColor(...EMERALD_DARK);
    doc.rect(0, 0, W, 14, 'F');
    setFont(doc, 8, 'bold', WHITE);
    doc.text('AFROSPORTS STREAM — ENGAGEMENT CHARTS', W / 2, 9, { align: 'center' });

    let cy = 22;

    if (growthImg) {
      setFont(doc, 8, 'bold', EMERALD_DARK);
      doc.text('Viewer Growth', 15, cy);
      setFont(doc, 7, 'normal', MUTED_TEXT);
      doc.text(`Unique viewers and total plays — ${label}`, 15, cy + 5);
      cy += 9;
      doc.addImage(growthImg, 'PNG', 15, cy, W - 30, 60);
      cy += 66;
    }

    if (peakImg) {
      setFont(doc, 8, 'bold', EMERALD_DARK);
      doc.text('Peak Viewing Hours', 15, cy);
      setFont(doc, 7, 'normal', MUTED_TEXT);
      doc.text('Play volume by hour of day — optimal broadcast scheduling', 15, cy + 5);
      cy += 9;
      doc.addImage(peakImg, 'PNG', 15, cy, W - 30, 55);
      cy += 61;
    }

    if (retentionImg) {
      setFont(doc, 8, 'bold', EMERALD_DARK);
      doc.text('Retention Signals', 15, cy);
      setFont(doc, 7, 'normal', MUTED_TEXT);
      doc.text('Reminders set + My List adds over time', 15, cy + 5);
      cy += 9;
      doc.addImage(retentionImg, 'PNG', 15, cy, W - 30, 50);
    }
  }

  // ── Footer on all pages ────────────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFillColor(...LIGHT_BG);
    doc.rect(0, 284, W, 13, 'F');
    drawHRule(doc, 284);
    setFont(doc, 7, 'normal', MUTED_TEXT);
    doc.text('AfroSports Stream — Confidential. For league and sponsor use only.', 15, 291);
    doc.text(`Page ${p} of ${totalPages}`, W - 15, 291, { align: 'right' });
  }

  const filename = `afrosports-report-${label.toLowerCase().replace(/\s+/g, '-')}-${now.toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  setExporting(false);
}
